import { useCallback, useEffect, useRef, useState } from "react";
import { normalizeApiError } from "../utils/errors";

export default function useApiRequest(initialData = null) {
  const initialDataRef = useRef(initialData);
  const [data, setData] = useState(() => initialDataRef.current);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    initialDataRef.current = initialData;
  }, [initialData]);

  const abort = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
  }, []);

  const run = useCallback(
    async (requestFn, options = {}) => {
      const { preserveData = true } = options;

      abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      if (!preserveData) {
        setData(initialDataRef.current);
      }

      setLoading(true);
      setError(null);

      try {
        const result = await requestFn({ signal: controller.signal });

        if (!mountedRef.current || controller.signal.aborted) {
          return undefined;
        }

        setData(result);
        return result;
      } catch (errorValue) {
        const normalizedError = normalizeApiError(errorValue);

        if (normalizedError.isAbort) {
          return undefined;
        }

        if (mountedRef.current) {
          setError(normalizedError);
        }

        throw normalizedError;
      } finally {
        if (mountedRef.current && controllerRef.current === controller) {
          setLoading(false);
        }
      }
    },
    [abort],
  );

  const reset = useCallback(() => {
    setData(initialDataRef.current);
    setError(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      abort();
    };
  }, [abort]);

  return {
    data,
    setData,
    loading,
    error,
    setError,
    run,
    abort,
    reset,
  };
}
